import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, STATUSES, STATUS_META, itDate } from "@/lib/api";
import { toast } from "sonner";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CHIAVI = [...STATUSES, "NON_REGISTRATO"];
const ETICHETTA = { NON_REGISTRATO: "Non registrato" };
const META = { ...STATUS_META, NON_REGISTRATO: { short: "—", cell: "bg-secondary text-foreground", dot: "bg-muted-foreground" } };
const label = (k) => ETICHETTA[k] || k.charAt(0) + k.slice(1).toLowerCase();
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function StaffSituation() {
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [vista, setVista] = useState("giorno");
  const [reparto, setReparto] = useState("all");
  const [reparti, setReparti] = useState([]);
  const [data, setData] = useState(null);
  const [aperta, setAperta] = useState(null);

  useEffect(() => { api.get("/departments").then((r) => setReparti(r.data)).catch(() => {}); }, []);

  const carica = useCallback(() => {
    api.get("/staff-situation", { params: { day, department: reparto } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [day, reparto]);
  useEffect(() => { carica(); }, [carica]);

  const sposta = (passo) => {
    const d = new Date(`${day}T12:00:00`);
    d.setDate(d.getDate() + (vista === "giorno" ? passo : passo * 7));
    setDay(d.toISOString().slice(0, 10));
  };

  const titolo = useMemo(() => {
    if (!data) return "";
    if (vista === "giorno") return itDate(data.day);
    return `${itDate(data.days[0])} — ${itDate(data.days[6])}`;
  }, [data, vista]);

  return (
    <div className="space-y-4" data-testid="staff-situation">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Situazione Dipendenti</h1>
        <p className="text-sm text-muted-foreground">Presenze, assenze e causali giustificative — sola consultazione</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-sm border border-border overflow-hidden">
          {["giorno", "settimana"].map((v) => (
            <button key={v} onClick={() => setVista(v)} data-testid={`situation-view-${v}`}
              className={`h-10 px-4 text-sm font-semibold uppercase transition-colors duration-150 ${
                vista === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
              {v}
            </button>
          ))}
        </div>
        <button onClick={() => sposta(-1)} data-testid="situation-prev" title="Precedente"
          className="h-10 w-10 flex items-center justify-center rounded-sm border border-border hover:bg-secondary">
          <CaretLeft size={16} />
        </button>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="situation-date"
          className="h-10 px-3 rounded-sm border border-border bg-background text-sm" />
        <button onClick={() => sposta(1)} data-testid="situation-next" title="Successivo"
          className="h-10 w-10 flex items-center justify-center rounded-sm border border-border hover:bg-secondary">
          <CaretRight size={16} />
        </button>
        <select value={reparto} onChange={(e) => setReparto(e.target.value)} data-testid="situation-department"
          className="h-10 px-3 rounded-sm border border-border bg-background text-sm">
          <option value="all">Tutti i reparti</option>
          {reparti.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={() => setDay(new Date().toISOString().slice(0, 10))} data-testid="situation-today"
          className="h-10 px-4 rounded-sm border border-border text-sm font-semibold hover:bg-secondary">Oggi</button>
        <span className="ml-auto font-mono text-sm text-muted-foreground" data-testid="situation-period">{titolo}</span>
      </div>

      {!data ? <div className="text-sm text-muted-foreground">Caricamento…</div> : vista === "giorno" ? (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="situation-day-cards">
          {CHIAVI.map((k) => (
            <button key={k} onClick={() => setAperta(k)} data-testid={`situation-card-${k}`}
              className="text-left rounded-sm border border-border bg-card p-4 hover:border-primary transition-colors duration-150">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <span className={`h-2 w-2 rounded-full ${META[k].dot}`} />{label(k)}
              </div>
              <div className="mt-1 text-3xl font-head font-bold tabular-nums">{data.counts[k] ?? 0}</div>
              <div className="text-[11px] text-muted-foreground">clicca per l'elenco</div>
            </button>
          ))}
          <div className="rounded-sm border border-border bg-secondary/40 p-4" data-testid="situation-card-total">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Dipendenti attivi</div>
            <div className="mt-1 text-3xl font-head font-bold tabular-nums">{data.total}</div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-sm border border-border bg-card">
            <table className="w-full text-sm bc-no-cards tbl-sticky" data-testid="situation-week-grid">
              <thead>
                <tr className="bg-secondary/60 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Dipendente</th>
                  {data.days.map((g, i) => (
                    <th key={g} className="px-2 py-2 text-center whitespace-nowrap">{DOW[i]} {g.slice(8, 10)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.grid.map((r) => (
                  <tr key={r.employee_id} className="border-t border-border" data-testid={`situation-week-row-${r.employee_id}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span className="font-medium">{r.last_name} {r.first_name}</span>
                      <span className="ml-2 text-[11px] text-muted-foreground">{r.department}</span>
                    </td>
                    {data.days.map((g) => {
                      const s = r.days[g];
                      const m = META[s || "NON_REGISTRATO"];
                      return (
                        <td key={g} className="px-1 py-1.5 text-center">
                          <span title={s || "Non registrato"}
                            className={`inline-block w-9 rounded-sm py-0.5 text-[11px] font-bold ${m.cell}`}>{m.short}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto rounded-sm border border-border bg-card">
            <table className="w-full text-sm bc-no-cards" data-testid="situation-week-totals">
              <thead>
                <tr className="bg-secondary/60 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Causale</th>
                  {data.days.map((g, i) => <th key={g} className="px-2 py-2 text-center">{DOW[i]} {g.slice(8, 10)}</th>)}
                </tr>
              </thead>
              <tbody>
                {CHIAVI.map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td className="px-3 py-1.5 flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${META[k].dot}`} />{label(k)}
                    </td>
                    {data.days.map((g) => (
                      <td key={g} className="px-2 py-1.5 text-center tabular-nums">{data.week_counts[g]?.[k] ?? 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!aperta} onOpenChange={(o) => !o && setAperta(null)}>
        <DialogContent className="max-w-lg" data-testid="situation-list-dialog">
          <DialogHeader>
            <DialogTitle>{aperta ? label(aperta) : ""} — {itDate(data?.day)}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
            {(data?.lists?.[aperta] || []).map((e) => (
              <div key={e.employee_id} className="py-2" data-testid={`situation-list-item-${e.employee_id}`}>
                <div className="font-medium">{e.last_name} {e.first_name}</div>
                <div className="text-xs text-muted-foreground">{e.department}{e.notes ? ` · ${e.notes}` : ""}</div>
              </div>
            ))}
            {!(data?.lists?.[aperta] || []).length && (
              <div className="py-4 text-sm text-muted-foreground">Nessun dipendente in questa causale.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
